import { type Client } from '@microsoft/microsoft-graph-client';
import type {
  SubscribedSku,
  UserLicenseDetail,
  LicenseSync,
  Application,
  AppAssignment,
  SPListItem,
} from '@/models/types';
import {
  getListItems,
  createListItem,
  updateListItem,
} from './sharePointService';
import { SKU_FRIENDLY_NAMES } from '@/utils/constants';

export async function fetchSubscribedSkus(
  client: Client,
): Promise<SubscribedSku[]> {
  const response = await client.api('/subscribedSkus').get();
  return response.value;
}

export async function fetchUserLicenses(
  client: Client,
): Promise<UserLicenseDetail[]> {
  const users: UserLicenseDetail[] = [];

  let response = await client
    .api('/users')
    .select('id,displayName,assignedLicenses')
    .top(999)
    .get();

  while (response) {
    users.push(...response.value);
    if (response['@odata.nextLink']) {
      response = await client.api(response['@odata.nextLink']).get();
    } else {
      break;
    }
  }

  return users;
}

export interface LicenseSyncResult {
  skusProcessed: number;
  appsCreated: number;
  appsUpdated: number;
  syncLogEntries: number;
}

export async function syncLicensesToSharePoint(
  client: Client,
  skus: SubscribedSku[],
): Promise<LicenseSyncResult> {
  const result: LicenseSyncResult = {
    skusProcessed: 0,
    appsCreated: 0,
    appsUpdated: 0,
    syncLogEntries: 0,
  };

  // Get existing auto-synced apps (filter client-side — SP boolean fields not indexed)
  const allApps = await getListItems<Application>(client, 'OC_Applications');
  const existingApps = allApps.filter((a) => a.fields.IsAutoSynced === true);
  const appsBySku = new Map<string, SPListItem<Application>>();
  for (const app of existingApps) {
    if (app.fields.MicrosoftSKU) {
      appsBySku.set(app.fields.MicrosoftSKU, app);
    }
  }

  const now = new Date().toISOString();

  for (const sku of skus) {
    if (sku.prepaidUnits.enabled === 0 && sku.consumedUnits === 0) continue;

    const friendlyName =
      SKU_FRIENDLY_NAMES[sku.skuPartNumber] ?? sku.skuPartNumber;
    const totalLicenses = sku.prepaidUnits.enabled;
    const assignedCount = sku.consumedUnits;
    const availableCount = totalLicenses - assignedCount;

    // Update or create the application entry
    const existingApp = appsBySku.get(sku.skuPartNumber);
    if (existingApp) {
      await updateListItem<Application>(
        client,
        'OC_Applications',
        existingApp.id,
        { Title: friendlyName },
      );
      result.appsUpdated++;
    } else {
      await createListItem<Application>(client, 'OC_Applications', {
        Title: friendlyName,
        Vendor: 'Microsoft',
        Category: 'Productivity',
        LicenseModel: 'PerUser',
        MonthlyCostPerLicense: 0, // admin enters actual cost
        BillingCycle: 'Monthly',
        MicrosoftSKU: sku.skuPartNumber,
        IsAutoSynced: true,
      });
      result.appsCreated++;
    }

    // Write sync log entry
    await createListItem<LicenseSync>(client, 'OC_LicenseSync', {
      AppName: friendlyName,
      SKU: sku.skuPartNumber,
      TotalLicenses: totalLicenses,
      AssignedCount: assignedCount,
      AvailableCount: availableCount,
      MonthlyCost: 0,
      SyncTimestamp: now,
    });
    result.syncLogEntries++;
    result.skusProcessed++;
  }

  return result;
}

export async function syncUserLicenseAssignments(
  client: Client,
  userLicenses: UserLicenseDetail[],
  skus: SubscribedSku[],
): Promise<number> {
  // Build SKU ID to part number map
  const skuMap = new Map<string, string>();
  for (const sku of skus) {
    skuMap.set(sku.skuId, sku.skuPartNumber);
  }

  // Get employee mapping (email → SP item id) — unused for now, kept for future
  // The license sync matches by EntraObjectId below instead

  // Get app mapping (SKU → SP item id) — filter client-side
  const allApps = await getListItems<Application>(client, 'OC_Applications');
  const apps = allApps.filter((a) => a.fields.IsAutoSynced === true);
  const appBySku = new Map<string, string>();
  for (const app of apps) {
    if (app.fields.MicrosoftSKU) {
      appBySku.set(app.fields.MicrosoftSKU, app.id);
    }
  }

  // Get existing assignments
  const existingAssignments = await getListItems<AppAssignment>(
    client,
    'OC_AppAssignments',
  );
  const assignmentKey = (empId: number, appId: number) => `${empId}:${appId}`;
  const existingKeys = new Set(
    existingAssignments.map((a) =>
      assignmentKey(a.fields.EmployeeId, a.fields.AppId),
    ),
  );

  // We need employee EntraObjectId → SP item ID mapping
  const empByEntraId = new Map<string, string>();
  const allEmployees = await getListItems<{ EntraObjectId: string }>(
    client,
    'OC_Employees',
  );
  for (const emp of allEmployees) {
    empByEntraId.set(emp.fields.EntraObjectId, emp.id);
  }

  let created = 0;
  for (const user of userLicenses) {
    const empItemId = empByEntraId.get(user.id);
    if (!empItemId) continue;

    for (const license of user.assignedLicenses) {
      const skuPartNumber = skuMap.get(license.skuId);
      if (!skuPartNumber) continue;

      const appItemId = appBySku.get(skuPartNumber);
      if (!appItemId) continue;

      const key = assignmentKey(Number(empItemId), Number(appItemId));
      if (!existingKeys.has(key)) {
        await createListItem<AppAssignment>(client, 'OC_AppAssignments', {
          EmployeeId: Number(empItemId),
          AppId: Number(appItemId),
          LicenseType: skuPartNumber,
          AssignedDate: new Date().toISOString(),
          Status: 'Active',
          AssignedBy: 'Auto-Sync',
        });
        existingKeys.add(key);
        created++;
      }
    }
  }

  return created;
}
